import { useState } from "react";
import { useLocation } from "react-router-dom";
import { trackWhatsAppClick } from "@/lib/gads";
import { isTodayQuoteRoute } from "@/components/TodayQuoteBar";

/**
 * Animated pipe-shaped WhatsApp CTA.
 * Larger, more prominent icon with stronger motion; clicking opens WhatsApp chat.
 */
const whatsappUrl =
  "https://wa.me/919894105887?text=Hi%2C%20I%20would%20like%20to%20inquire%20about%20your%20steel%20pipes%20and%20tubes.";

const whatsappAppUrl =
  "whatsapp://send?phone=919894105887&text=Hi%2C%20I%20would%20like%20to%20inquire%20about%20your%20steel%20pipes%20and%20tubes.";

const WhatsAppButton = () => {
  const [isOpening, setIsOpening] = useState(false);
  const { pathname } = useLocation();

  if (isTodayQuoteRoute(pathname)) return null;

  const openWhatsApp = () => {
    if (isOpening) return;
    trackWhatsAppClick();
    setIsOpening(true);

    const isMobileOrTablet = /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(navigator.userAgent);

    if (isMobileOrTablet) {
      window.location.href = whatsappAppUrl;
      window.setTimeout(() => {
        window.location.href = whatsappUrl;
      }, 900);
      return;
    }

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    window.setTimeout(() => setIsOpening(false), 1500);
  };

  return (
    <>
      <style>{`
        @keyframes pipe-bounce {
          0%, 100% { transform: translateY(0) rotate(-12deg) scale(1); }
          25%      { transform: translateY(-18px) rotate(12deg) scale(1.05); }
          50%      { transform: translateY(-8px) rotate(-10deg) scale(1); }
          75%      { transform: translateY(-14px) rotate(14deg) scale(1.04); }
        }
        @keyframes pipe-shimmer {
          0%, 100% { opacity: 0.6; transform: translateX(0); }
          50%      { opacity: 1; transform: translateX(6px); }
        }
        @keyframes pipe-spinner {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .pipe-cta {
          transform-origin: center;
        }
        .pipe-cta .pipe-icon {
          animation: pipe-bounce 2.2s ease-in-out infinite;
          transform-origin: center;
        }
        .pipe-cta .pipe-shine {
          animation: pipe-shimmer 2.2s ease-in-out infinite;
        }
        .pipe-cta:hover {
          animation-play-state: paused;
        }
        .pipe-cta:hover .pipe-icon {
          animation-play-state: paused;
          transform: scale(1.12) rotate(-4deg);
        }
        .pipe-cta.opening .pipe-icon {
          animation-play-state: paused;
        }
        .pipe-spinner {
          animation: pipe-spinner 0.8s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .pipe-cta, .pipe-cta .pipe-icon, .pipe-cta .pipe-shine { animation: none !important; }
        }
      `}</style>
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => {
          event.preventDefault();
          openWhatsApp();
        }}
        aria-label={isOpening ? "Opening WhatsApp chat" : "Chat on WhatsApp about steel pipes"}
        aria-busy={isOpening}
        className={`pipe-cta group fixed bottom-6 right-5 z-[60] flex items-center gap-2 ${isOpening ? "opening" : ""}`}
      >
        <span className="pointer-events-none absolute right-full mr-3 whitespace-nowrap rounded-md bg-foreground px-3 py-1.5 text-sm font-medium text-background opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
          Chat on WhatsApp
          <span className="absolute left-full top-1/2 -mt-1 -ml-1 h-2 w-2 rotate-45 bg-foreground" />
        </span>
        <span className="relative flex items-center justify-center">
          <svg
            className="pipe-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="52"
            height="52"
            viewBox="0 0 64 64"
            fill="none"
            aria-hidden="true"
          >
            {/* Pipe body */}
            <rect x="10" y="23" width="44" height="18" rx="3" fill="#d1d5db" stroke="#1f2937" strokeWidth="2.5" />
            {/* Left flange */}
            <rect x="4" y="16" width="10" height="32" rx="2" fill="#9ca3af" stroke="#1f2937" strokeWidth="2.5" />
            {/* Right flange */}
            <rect x="50" y="16" width="10" height="32" rx="2" fill="#9ca3af" stroke="#1f2937" strokeWidth="2.5" />
            {/* Bolt holes */}
            <circle cx="9" cy="22" r="1.5" fill="#374151" />
            <circle cx="9" cy="42" r="1.5" fill="#374151" />
            <circle cx="55" cy="22" r="1.5" fill="#374151" />
            <circle cx="55" cy="42" r="1.5" fill="#374151" />
            {/* Shine */}
            <rect className="pipe-shine" x="14" y="26" width="36" height="4" rx="2" fill="#ffffff" opacity="0.7" />
          </svg>
          {isOpening && (
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-[2px]">
              <svg className="pipe-spinner" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeDasharray="45 25" />
              </svg>
            </span>
          )}
        </span>
      </a>
    </>
  );
};

export default WhatsAppButton;
